import { createContext, useContext, useMemo } from 'react'
import { SettingsPageTable } from '../../components/tables/types'
import {
  BreadcrumbObjectType,
  useGetDetailedChartQuery,
  useGetSettingsPageTableDetailsQuery,
  useGetWorkflowNameQuery,
} from '../generated/apollo-operations'
import { useBreadcrumbs } from './useBreadcrumbs'
import { usePage } from './usePage'
import { useTypedTranslation } from './useTypedTranslation'

export type BackLinkContextType = {
  objectId?: string
  objectType: BreadcrumbObjectType
  name?: string
  icon?: SettingsPageTable['icon']
  workflow?: {
    id?: string
    name?: string
  }
  home?: boolean
}

export function BackLinkProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTypedTranslation()
  const { objectId, objectType } = usePage()
  const { breadcrumbs } = useBreadcrumbs()
  // Find the table in the breadcrumb trail. Route crumbs like "Table Settings"
  // or "Exports" may be appended after the API breadcrumbs, so we search by
  // workspaceType instead of checking positional indices.
  const tableId = useMemo(() => {
    const tableCrumb = breadcrumbs.findLast((b) => b.workspaceType === BreadcrumbObjectType.TABLE)
    const hasTableRow = breadcrumbs.some((b) => b.workspaceType === BreadcrumbObjectType.TABLE_ROW)

    if (hasTableRow && tableCrumb) {
      // Viewing a table row — use the parent table's ID from the table breadcrumb link
      return tableCrumb.link.split('/').at(-1) || null
    }
    if (tableCrumb) {
      // Viewing a table directly (detail, settings, or exports)
      return objectId
    }
    return null
  }, [breadcrumbs, objectId])

  const { data: tableData } = useGetSettingsPageTableDetailsQuery({
    variables: {
      tableId: tableId || '',
    },
    skip: !tableId,
  })
  const { data: chartData } = useGetDetailedChartQuery({
    variables: {
      chartId:
        objectType === BreadcrumbObjectType.CHART || objectType === BreadcrumbObjectType.PIVOT_TABLE
          ? (objectId as string)
          : '',
    },
    skip:
      (objectType !== BreadcrumbObjectType.CHART &&
        objectType !== BreadcrumbObjectType.PIVOT_TABLE) ||
      !objectId,
  })

  const { data: workflowData } = useGetWorkflowNameQuery({
    variables: {
      workflowId: objectType === BreadcrumbObjectType.WORKFLOW ? (objectId as string) : '',
    },
    skip: objectType !== BreadcrumbObjectType.WORKFLOW || !objectId,
  })

  const backLinkObjectType = useMemo(() => {
    if (
      objectType === BreadcrumbObjectType.CHART ||
      objectType === BreadcrumbObjectType.PIVOT_TABLE
    ) {
      return BreadcrumbObjectType.WORKSPACE
    }
    if (objectType === BreadcrumbObjectType.TABLE_ROW) {
      return BreadcrumbObjectType.TABLE
    }
    if (breadcrumbs.length === 0) {
      return BreadcrumbObjectType.ORGANIZATION
    }
    return objectType || BreadcrumbObjectType.TABLE
  }, [breadcrumbs, objectType])

  const icon = useMemo(() => {
    return tableData?.table.icon || null
  }, [tableData])

  const name = useMemo(() => {
    if (objectType === BreadcrumbObjectType.FORM) {
      return t('form.forms')
    }
    if (objectType === BreadcrumbObjectType.WORKFLOW) {
      return workflowData?.workflow.name || t('form.settings.workflows')
    }
    if (
      objectType === BreadcrumbObjectType.TABLE ||
      objectType === BreadcrumbObjectType.TABLE_ROW
    ) {
      return tableData?.table.name
    }
    if (
      objectType === BreadcrumbObjectType.CHART ||
      objectType === BreadcrumbObjectType.PIVOT_TABLE
    ) {
      return chartData?.chart.views.at(0)?.parentWorkspace.name || t('charts.dashboard')
    }
    return t('common.home')
  }, [objectType, workflowData?.workflow.name, tableData?.table.name, chartData?.chart.views, t])

  const backlinkObjectId = useMemo(() => {
    if (objectType === BreadcrumbObjectType.TABLE_ROW) {
      return tableData?.table.id
    }
    if (
      objectType === BreadcrumbObjectType.CHART ||
      objectType === BreadcrumbObjectType.PIVOT_TABLE
    ) {
      return chartData?.chart.views.at(0)?.parentWorkspace.id
    }
    return objectId
  }, [objectType, objectId, chartData?.chart.views, tableData?.table.id])

  return (
    <BackLinkContext.Provider
      value={{
        objectId:
          objectType === BreadcrumbObjectType.TABLE_ROW ? tableData?.table.id : backlinkObjectId,
        objectType: backLinkObjectType,
        name,
        icon,
        workflow: objectType === BreadcrumbObjectType.WORKFLOW ? {} : undefined,
        home: true,
      }}
    >
      {children}
    </BackLinkContext.Provider>
  )
}

/**
 * Provides information about page path
 */
export const useBackLink = () => {
  const context = useContext(BackLinkContext)
  if (!context) {
    throw new Error('useBackLink must be used within a BackLinkProvider')
  }
  return context
}

const BackLinkContext = createContext<BackLinkContextType | undefined>(undefined)

export default useBackLink
